import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { BookOpen } from 'lucide-react-native';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { twJoin } from 'tailwind-merge';

import {
  groupUnits,
  parseAnswersMarkdown,
  type Block,
  type InlineNode,
  type ListItemNode,
} from './answersMarkdownParser';
import { referenceBlock, splitReferenceSection } from './answersReferences';
import { searchMatchSegments } from './searchMatches';

import { SEARCH_MATCH_CLASS } from '@/components/Highlight';
import { BulletDot } from '@/components/ui/bullet-dot';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { openMessageLink } from '@/util/messageLinks';

type LinkEvent = { preventDefault?: () => void };

/**
 * A link inside a run of text. On web it is a real anchor, so it can be reached and opened from
 * the keyboard and a middle click works; the press handler stops the anchor's own navigation
 * and opens the link the way chat links open, in a new tab. On native it is pressable text.
 */
/**
 * Told about every link a reader opens inside it, before the link opens. A list that wants to know
 * which of its links was used, such as an answer's references for analytics, provides it.
 */
export const LinkPressContext = createContext<((href: string) => void) | null>(null);

/**
 * The color class of the links inside it. Body text links stay the brand green. An answer's
 * references set the blue of their heading, so the section reads as one piece.
 */
export const LinkClassContext = createContext('text-primary-700');

/**
 * The search the reader opened the post from, marked wherever its words appear in the text inside
 * it, the way the result they tapped marks them. Empty when the post was not opened from a search.
 */
export const SearchQueryContext = createContext('');

function SearchMatchedText({ text }: { text: string }) {
  const query = useContext(SearchQueryContext);

  const segments = useMemo(() => searchMatchSegments(text, query), [text, query]);

  return (
    <>
      {segments.map((segment, index) =>
        segment.isMatch ? (
          <Text key={index} className={SEARCH_MATCH_CLASS}>
            {segment.text}
          </Text>
        ) : (
          segment.text
        )
      )}
    </>
  );
}

function InlineLink({
  href,
  label,
  children,
}: {
  href: string;
  /** The spoken name, when the visible words alone would not say what the link opens. */
  label?: string;
  children: ReactNode;
}) {
  const onLinkPress = useContext(LinkPressContext);

  const linkClass = useContext(LinkClassContext);

  const open = (event?: LinkEvent) => {
    event?.preventDefault?.();

    onLinkPress?.(href);

    openMessageLink(href);
  };

  // The web Text is a plain span, which drops `href` and `onPress` and warns about both, so on
  // web the link is a real anchor drawn with the same classes.
  if (isWeb) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        onClick={open}
        className={twJoin('font-body underline', linkClass)}>
        {children}
      </a>
    );
  }

  return (
    <Text
      role="link"
      aria-label={label}
      accessibilityLabel={label}
      onPress={open}
      className={twJoin('font-body underline', linkClass)}>
      {children}
    </Text>
  );
}

function Inline({ nodes }: { nodes: InlineNode[] }) {
  return (
    <>
      {nodes.map((node, index) => {
        switch (node.kind) {
          case 'text':
            return <SearchMatchedText key={index} text={node.text} />;

          case 'break':
            return '\n';

          case 'strong':
            return (
              <Text key={index} className="font-body font-semibold">
                <Inline nodes={node.children} />
              </Text>
            );

          case 'emphasis':
            return (
              <Text key={index} className="font-body italic">
                <Inline nodes={node.children} />
              </Text>
            );

          case 'code':
            return (
              <Text key={index} className="font-mono">
                {node.text}
              </Text>
            );

          case 'link':
            return (
              <InlineLink key={index} href={node.href} label={node.label}>
                <Inline nodes={node.children} />
              </InlineLink>
            );
        }
      })}
    </>
  );
}

/** One run of inline text: a paragraph, a list item's line, or a citation. */
export function Line({ nodes, className }: { nodes: InlineNode[]; className: string }) {
  return (
    <Text
      className={twJoin(
        'font-body leading-normal web:whitespace-pre-line web:[overflow-wrap:anywhere]',
        className
      )}>
      <Inline nodes={nodes} />
    </Text>
  );
}

function ListBlock({
  block,
  className,
  gapClassName = 'gap-1.5',
}: {
  block: Extract<Block, { kind: 'list' }>;
  className: string;
  gapClassName?: string;
}) {
  return (
    <VStack role="list" className={gapClassName}>
      {block.items.map((item, index) => (
        <ListRow
          key={index}
          item={item}
          marker={block.ordered ? `${block.start + index}.` : null}
          className={className}
        />
      ))}
    </VStack>
  );
}

function ListRow({
  item,
  marker,
  className,
}: {
  item: ListItemNode;
  marker: string | null;
  className: string;
}) {
  return (
    <HStack role="listitem" className="items-start gap-3">
      {marker ? (
        <Text className={twJoin('min-w-5 font-body leading-normal', className)}>{marker}</Text>
      ) : (
        <BulletDot />
      )}

      <VStack className="min-w-0 flex-1 gap-1.5">
        {item.children.length > 0 ? <Line nodes={item.children} className={className} /> : null}

        {item.blocks.map((nested, index) => (
          <BlockView key={index} block={nested} className={className} />
        ))}
      </VStack>
    </HStack>
  );
}

export function BlockView({
  block,
  className,
  listGapClassName,
}: {
  block: Block;
  className: string;
  /** The space between list items, when a list wants more than the reading default. */
  listGapClassName?: string;
}) {
  if (block.kind === 'list') {
    return <ListBlock block={block} className={className} gapClassName={listGapClassName} />;
  }

  return (
    <Line
      nodes={block.children}
      className={twJoin(className, block.kind === 'heading' && 'font-semibold')}
    />
  );
}

/** A unit of blocks a fold may cut between: one block, or a label with the list it introduces. */
export function UnitView({ unit, className }: { unit: Block[]; className: string }) {
  return (
    <VStack className="gap-1.5">
      {unit.map((block, index) => (
        <BlockView key={index} block={block} className={className} />
      ))}
    </VStack>
  );
}

/**
 * The reading measure, about 72 characters of body text, so a line on a wide screen stays
 * comfortable to follow. Web only: the `ch` unit does not exist on native, where the screen is
 * narrow enough already.
 */
export const READING_MEASURE = 'web:max-w-[72ch]';

/**
 * A reference list under its label: one list, each paper a single link to its DOI, PubMed or web
 * page where it carries one, and plain text where it does not. The list is a real list to a screen
 * reader, and a link's name is the citation it opens.
 */
export function ReferencesGroup({ children }: { children: ReactNode }) {
  return (
    <VStack role="group" aria-label="References" className="gap-1">
      <HStack className="items-center gap-1.5">
        <Icon as={BookOpen} size={14} className="text-secondary-800" />

        <Text size="xs" className="font-body font-semibold text-secondary-800">
          References
        </Text>
      </HStack>

      {children}
    </VStack>
  );
}

/**
 * Draws the text of a turn, the question or a reference list: paragraphs, bullet and numbered
 * lists nested under each other, bold, italic, headings as bold lines, and links, including the
 * PubMed and doi.org links built from a PMID or a bare DOI. Everything goes through the app's
 * Text, with the bullet from the shared `BulletDot`, so type and color stay with the design
 * tokens. Lists are real lists to a screen reader on web.
 *
 * Body text runs at a 1.5 line height and paragraphs sit a line apart. A label that ends in a
 * colon sits close to the list it introduces.
 */
export function AnswersMarkdown({
  text,
  className = 'text-base text-typography-900',
  hideReferences = false,
}: {
  text: string;
  className?: string;
  /**
   * Leaves out a reference list written into the text, for an answer whose references the API
   * already lists in their own section, so no paper shows twice.
   */
  hideReferences?: boolean;
}) {
  const { units, references } = useMemo(() => {
    const section = splitReferenceSection(text);

    return {
      units: groupUnits(parseAnswersMarkdown(section ? section.body : text)),
      references: section && !hideReferences ? referenceBlock(section) : null,
    };
  }, [text, hideReferences]);

  return (
    <VStack className={twJoin('gap-4', READING_MEASURE)}>
      {units.map((unit, index) => (
        <UnitView key={index} unit={unit} className={className} />
      ))}

      {references ? (
        <ReferencesGroup>
          <BlockView block={references} className="text-sm text-typography-700" />
        </ReferencesGroup>
      ) : null}
    </VStack>
  );
}
